import { useState, useRef, ReactNode, useCallback } from "react";
import useOutsideClick from "@/hooks/useOutsideClick";
import ChevronIcon from "@/common/ChevronIcon";

interface DropdownItem {
  name: string;
  value: string;
}

interface DropdownProps {
  items: readonly DropdownItem[];
  displayValue: string;
  selectedValue?: string;
  onSelect: (value: string) => void;
  icon?: ReactNode;
  isTop?: boolean;
}

/**
 * 다국어 선택을 위한 드롭다운 컴포넌트
 * - 아이콘, 스타일, 아웃사이드 클릭 등 기능 제공
 */
const Dropdown = ({
  items,
  displayValue,
  selectedValue,
  onSelect,
  icon,
  isTop = false,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(
    (value: string) => {
      onSelect(value);
      setIsOpen(false);
    },
    [onSelect],
  );

  const toggleDropdown = useCallback(() => setIsOpen((prev) => !prev), []);
  const closeDropdown = useCallback(() => setIsOpen(false), []);

  useOutsideClick(dropdownRef, closeDropdown, isOpen);

  return (
    <div ref={dropdownRef} className="relative inline-block z-[150]">
      <button
        onClick={toggleDropdown}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[0.875rem] font-[600] transition-colors duration-300 cursor-pointer focus:outline-none pointer-events-auto ${
          isTop
            ? "border-white text-white hover:text-white/80"
            : "border-gray-300 text-gray-700 hover:text-gray-900"
        }`}
      >
        {icon}
        <span>{displayValue}</span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <ul className="absolute right-0 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden z-50">
          {items.map((item) => (
            <li key={item.value}>
              <button
                onClick={() => handleSelect(item.value)}
                className={`w-full px-4 py-2 text-sm text-center font-semibold transition-colors cursor-pointer hover:bg-gray-50 ${
                  (selectedValue ? item.value === selectedValue : item.name === displayValue)
                    ? "text-blue-500"
                    : "text-gray-700"
                }`}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
